import React, { useState, useCallback } from 'react'
import * as Sentry from '@sentry/react-native'
import { StyleSheet, View, TouchableOpacity } from 'react-native'
import { Text, Icon, IconButton } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/native'
import { Row, PaginatedFlatList, CoRankLabel, UserLink } from '../../../../@ui'
import { SortOptions, SortOption } from '../../../../@ui/buttons/sort.button'
import { MelddSearchBar } from '../../../../@ui/form/meldd-search.input'
import {
  useCollaboratorsQuery,
  useRemoveCollaboratorFromListMutation,
  CollaboratorsQuery,
} from '../../../../generated/graphql'
import { IconAssignTask } from '../../../../utils/meldd-icons'
import { AppNavigation } from '../../../../navigation/types'
import { navigateToTaskTarget } from '../../../tasks/task.types'
import { createStyle } from '../../../../contexts/global-styles.provider'
import { AddCollaboratorToListModal } from '../components/add-collaborator-to-list.modal'
import { CollaboratorListTag } from '../components/collaborator-list-tag'
import { TimelineCompactRow } from '../components/timeline-base.list'
import { navigateToTarget } from '../../../../utils/target'

type CollaboratorUser = NonNullable<
  NonNullable<CollaboratorsQuery['collaborators']>['users']
>[number]

const buildSortOptions = (t: (key: string) => string): SortOption<string>[] => [
  {
    field: 'createdAt',
    label: t('sort-newest-first'),
    direction: 'DESC',
    icon: 'sort-calendar-descending',
  },
  {
    field: 'createdAt',
    label: t('sort-oldest-first'),
    direction: 'ASC',
    icon: 'sort-calendar-ascending',
  },
  {
    field: 'username',
    label: t('sort-name-a-z'),
    direction: 'ASC',
    icon: 'sort-alphabetical-ascending',
  },
  {
    field: 'username',
    label: t('sort-name-z-a'),
    direction: 'DESC',
    icon: 'sort-alphabetical-descending',
  },
  {
    field: 'coRank',
    label: t('sort-corank-highest'),
    direction: 'DESC',
    icon: 'sort-numeric-descending',
  },
  {
    field: 'coRank',
    label: t('sort-corank-lowest'),
    direction: 'ASC',
    icon: 'sort-numeric-ascending',
  },
]

type IncompleteTask = {
  id: string
  title: string
  type: string
  targetId: string
  targetType: string
}

const IncompleteTasksIcon: React.FC<{
  incompleteTasks: IncompleteTask[]
  navigation: ReturnType<typeof useNavigation<AppNavigation>>
}> = ({ incompleteTasks, navigation }) => {
  const dynamicStyles = useStyle()
  const incompleteTasksCount = incompleteTasks.length
  const firstIncompleteTask = incompleteTasks[0]

  if (incompleteTasksCount === 0) {
    return null
  }

  const handlePress = () => {
    if (firstIncompleteTask) {
      navigateToTaskTarget({
        task: {
          targetType: firstIncompleteTask.targetType as any,
          targetId: firstIncompleteTask.targetId,
        },
        navigation,
      })
    }
  }

  return (
    <View style={dynamicStyles.taskIconContainer}>
      <TouchableOpacity
        onPress={handlePress}
        style={dynamicStyles.taskIconButton}
      >
        <Icon source={IconAssignTask} size={18} />
      </TouchableOpacity>
      <View style={dynamicStyles.taskBadge}>
        <Text style={dynamicStyles.taskBadgeText}>{incompleteTasksCount}</Text>
      </View>
    </View>
  )
}

const staticStyles = StyleSheet.create({
  listSection: {
    flex: 1,
  },
  filtersRow: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  row: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  rowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  username: {
    fontWeight: '600',
  },
  corankContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  corankAvatar: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FFE5B4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  corank: {
    fontWeight: '600',
    fontSize: 14,
    opacity: 0.7,
  },
  name: {
    marginTop: 2,
  },
  bio: {
    marginTop: 2,
    fontStyle: 'italic',
  },
  listsRow: {
    marginTop: 4,
    flexWrap: 'wrap',
  },
})

type MyCollaboratorsListTabProps = {
  userId?: string
}

export const MyCollaboratorsListTab: React.FC<MyCollaboratorsListTabProps> = ({
  userId,
}) => {
  const { t } = useTranslation('nodes')
  const navigation = useNavigation<AppNavigation>()
  const [search, setSearch] = useState('')
  const [addToListCollaborator, setAddToListCollaborator] = useState<{
    collaboratorId: string
    username: string
  } | null>(null)
  const [sortField, setSortField] = useState<
    'username' | 'createdAt' | 'coRank'
  >('createdAt')
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC')
  const [limit] = useState(20)

  const { data, loading, refetch, fetchMore } = useCollaboratorsQuery({
    variables: {
      pagination: {
        offset: 0,
        limit,
        sortBy: sortField,
        sort: sortDirection,
      },
      search: search || undefined,
    },
  })

  const [removeFromList] = useRemoveCollaboratorFromListMutation()

  const handleRemoveFromList = useCallback(
    (collaboratorId: string, listId: string) => {
      removeFromList({
        variables: { input: { collaboratorId, listId } },
      })
        .then(() => refetch())
        .catch((err) => {
          console.error('removeCollaboratorFromList', err)
          try {
            Sentry.captureException(err)
          } catch {
            // noop
          }
        })
    },
    [removeFromList, refetch]
  )

  const collaborators = data?.collaborators.users || []
  const total = data?.collaborators.total || 0

  const handleSortChange = useCallback(
    (field: string, direction: 'ASC' | 'DESC') => {
      const typedField =
        (field as 'username' | 'createdAt' | 'coRank') || 'createdAt'
      setSortField(typedField)
      setSortDirection(direction)
      refetch({
        pagination: {
          offset: 0,
          limit,
          sortBy: typedField,
          sort: direction,
        },
        search: search || undefined,
      })
    },
    [refetch, limit, search]
  )

  const handleSearchChange = useCallback(
    async (text: string) => {
      setSearch(text)
      await refetch({
        pagination: {
          offset: 0,
          limit,
          sortBy: sortField,
          sort: sortDirection,
        },
        search: text || undefined,
      })
    },
    [refetch, limit, sortField, sortDirection]
  )

  const handleLoadMore = useCallback(
    async (params: { offset: number; limit: number }) => {
      if (loading) return

      await fetchMore({
        variables: {
          pagination: {
            offset: params.offset,
            limit: params.limit,
            sortBy: sortField,
            sort: sortDirection,
          },
          search: search || undefined,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.collaborators) return prev
          return {
            collaborators: {
              ...fetchMoreResult.collaborators,
              users: [
                ...prev.collaborators.users,
                ...fetchMoreResult.collaborators.users,
              ],
            },
          }
        },
      })
    },
    [loading, fetchMore, sortField, sortDirection, search]
  )

  const handleRefresh = useCallback(async () => {
    await refetch({
      pagination: {
        offset: 0,
        limit,
        sortBy: sortField,
        sort: sortDirection,
      },
      search: search || undefined,
    })
  }, [refetch, limit, sortField, sortDirection, search])

  return (
    <View style={staticStyles.listSection}>
      <Row
        justifyContent="space-between"
        alignItems="center"
        style={staticStyles.filtersRow}
      >
        <MelddSearchBar
          placeholder={t('search-collaborators') || t('articles.search')}
          value={search}
          onChangeText={handleSearchChange}
        />
        <SortOptions
          selectedField={sortField}
          selectedDirection={sortDirection}
          onSortChange={handleSortChange}
          options={buildSortOptions(t)}
        />
      </Row>

      <PaginatedFlatList
        data={collaborators}
        total={total}
        initialLimit={limit}
        style={staticStyles.listContainer}
        contentContainerStyle={staticStyles.listContent}
        onLoadMore={handleLoadMore}
        onRefresh={handleRefresh}
        keyExtractor={(item) => {
          const collaborator = item as CollaboratorUser
          return collaborator.id
        }}
        renderItem={({ item }) => {
          const collaborator = item as CollaboratorUser & {
            publicCoRank?: number | null
            collaboratorId?: string | null
            lists?: Array<{
              id: string
              name: string
              color?: string | null
            }> | null
            lastEvent?: {
              id: string
              title: string
              timestamp: string
              action: string
              targetType: string
              targetId: string
              nodeName?: string | null
            } | null
            incompleteTasks?: Array<{
              id: string
              title: string
              type: string
              targetId: string
              targetType: string
            }> | null
          }
          const incompleteTasks = collaborator.incompleteTasks || []
          const lists = collaborator.lists ?? []
          const lastEvent = collaborator.lastEvent ?? null
          return (
            <View style={staticStyles.row}>
              <View style={staticStyles.rowHeader}>
                <Row alignItems="center" columnGap={1}>
                  <UserLink user={collaborator} />
                </Row>
                <Row alignItems="center" columnGap={8}>
                  {!!collaborator.publicCoRank ? (
                    <CoRankLabel corank={collaborator.publicCoRank} />
                  ) : null}
                  <IncompleteTasksIcon
                    incompleteTasks={incompleteTasks}
                    navigation={navigation}
                  />
                  {collaborator.collaboratorId ? (
                    <IconButton
                      icon="tag-outline"
                      size={20}
                      onPress={() =>
                        setAddToListCollaborator({
                          collaboratorId: collaborator.collaboratorId ?? '',
                          username: collaborator.username ?? '',
                        })
                      }
                      accessibilityLabel={t('add-to-list', {
                        username: collaborator.username ?? '',
                      })}
                    />
                  ) : null}
                </Row>
              </View>

              {collaborator.bio ? (
                <Text style={staticStyles.bio}>{collaborator.bio}</Text>
              ) : null}
              {collaborator.lastEvent ? (
                <TimelineCompactRow
                  item={collaborator.lastEvent}
                  onPress={() => {
                    if (
                      collaborator.lastEvent?.targetId &&
                      collaborator.lastEvent?.targetType
                    ) {
                      navigateToTarget({
                        target: {
                          type: collaborator.lastEvent.targetType as any,
                          id: collaborator.lastEvent.targetId,
                        },
                        navigation,
                      })
                    }
                  }}
                />
              ) : null}

              {lists.length > 0 ? (
                <Row
                  alignItems="center"
                  style={staticStyles.listsRow}
                  columnGap={2}
                >
                  {lists.map((list) => (
                    <CollaboratorListTag
                      key={list.id}
                      list={{ name: list.name, color: list.color }}
                      editable
                      onRemove={() =>
                        collaborator.collaboratorId &&
                        handleRemoveFromList(
                          collaborator.collaboratorId,
                          list.id
                        )
                      }
                    />
                  ))}
                </Row>
              ) : null}
            </View>
          )
        }}
        emptyMessage={t('no-users-found') || 'No collaborators found'}
      />

      <AddCollaboratorToListModal
        visible={addToListCollaborator !== null}
        onDismiss={() => setAddToListCollaborator(null)}
        onAssigned={() => refetch()}
        collaborator={addToListCollaborator}
      />
    </View>
  )
}

const useStyle = createStyle(({ theme }) => ({
  taskIconContainer: {
    position: 'relative',
  },
  taskIconButton: {
    padding: 4,
  },
  taskBadge: {
    position: 'absolute',
    top: -5,
    right: 0,
    backgroundColor: theme.colors.danger.main,
    borderRadius: 10,
    minWidth: 15,
    height: 15,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 3,
  },
  taskBadgeText: {
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },
}))

export default MyCollaboratorsListTab
